import { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { useQuery } from '@tanstack/react-query'
import { Views, type View } from 'react-big-calendar'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Fab from '@mui/material/Fab'
import useMediaQuery from '@mui/material/useMediaQuery'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, IconButton, api, formatDate, RemoteContent, Title, useAuth, useSnackbar } from '@jupi/ui'
import { useAvailability, useAvailabilityMutations, useMyLessons } from '../../api/lessons'
import { useBookingLinkState } from '../../features/calendar/bookingNavigation'
import { AvailabilityPanel, OpenBookingSwitch } from '../../features/lessons/AvailabilityPanel'
import { blank, draftForDays, type AvailabilityDraft } from '../../features/lessons/availabilityDraft'
import { BOTTOM_BAR } from '../../config/layout'
import { clubInstant, clubParts } from '../../features/lessons/clubTime'
import { LessonsCalendar } from '../../features/lessons/LessonsCalendar'

// The span of the calendar the view shows, with a margin so stepping to the
// neighbouring period does not start from nothing.
function rangeFor(view: View, date: Date) {
  const unit = view === Views.MONTH ? 'month' : view === Views.DAY ? 'day' : view === Views.AGENDA ? 'month' : 'week'
  return {
    from: dayjs(date).startOf(unit).subtract(1, 'week').toDate(),
    to: dayjs(date).endOf(unit).add(1, 'week').toDate(),
  }
}

// An instructor's lessons in a calendar (week, day or month), filterable by
// student, with the time they have offered shaded in and editable beneath.
export default function MyLessonsPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const linkState = useBookingLinkState()
  const [searchParams, setSearchParams] = useSearchParams()
  const studentUserId = searchParams.get('student')
  const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  // A phone has room for a day, not a week of columns.
  const [view, setView] = useState<View>(() =>
    window.matchMedia('(max-width:599.95px)').matches ? Views.DAY : Views.WEEK,
  )
  const [date, setDate] = useState(() => new Date())
  const [draft, setDraft] = useState<AvailabilityDraft | null>(null)
  const clearDraft = useCallback(() => setDraft(null), [])

  const range = useMemo(() => rangeFor(view, date), [view, date])
  const lessons = useMyLessons({ ...range, studentUserId })
  // The filter lists everyone with a lesson in view, so a chosen student
  // stays in it when the range changes.
  const everyone = useMyLessons({ ...range })
  const availability = useAvailability(user?.id, range)
  const { add } = useAvailabilityMutations(user?.id)

  const feed = useQuery({
    queryKey: ['events', 'calendar-link'],
    queryFn: () => api.get<{ url: string }>('/api/events/calendar-link'),
    enabled: false,
  })
  const copyFeed = async () => {
    const url = (feed.data ?? (await feed.refetch()).data)?.url.replace(/\.ics$/, '/lessons.ics')
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      showSnackbar(t('lessons.feedCopied'))
    } catch {
      showSnackbar(url)
    }
  }

  const unit = view === Views.MONTH || view === Views.AGENDA ? 'month' : view === Views.DAY ? 'day' : 'week'
  const step = (direction: -1 | 1) => setDate(dayjs(date).add(direction, unit).toDate())
  const heading =
    unit === 'month'
      ? new Intl.DateTimeFormat(i18n.language, {
          month: 'long',
          year: 'numeric',
        }).format(date)
      : unit === 'day'
        ? formatDate(date)
        : `${formatDate(dayjs(date).startOf('week').toDate())} – ${formatDate(dayjs(date).endOf('week').toDate())}`

  const students = everyone.data?.students ?? []
  const setStudent = (id: string) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('student', id)
    else next.delete('student')
    setSearchParams(next, { replace: true })
  }

  return (
    <Box>
      <Title label={t('nav.myLessons')}>
        <Button
          component={RouterLink}
          to="/bookings/wizard"
          variant="contained"
          startIcon={<Icon icon="mdi:calendar-search" />}
        >
          {t('lessons.findTime')}
        </Button>
      </Title>

      {user ? (
        <OpenBookingSwitch instructorUserId={user.id} openBooking={availability.data?.openBooking ?? false} />
      ) : null}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select
          size="small"
          label={t('lessons.student')}
          value={studentUserId ?? ''}
          onChange={(e) => setStudent(e.target.value)}
          sx={{ minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
        >
          <MenuItem value="">{t('lessons.allStudents')}</MenuItem>
          {students.map((s) => (
            <MenuItem key={s.userId} value={s.userId}>
              {s.displayName}
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" startIcon={<Icon icon="mdi:calendar-sync-outline" />} onClick={copyFeed}>
          {t('lessons.copyFeed')}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Button size="small" variant="outlined" onClick={() => setDate(new Date())}>
          {t('common.today')}
        </Button>
        <IconButton size="small" onClick={() => step(-1)} aria-label={t('calendar.previous')}>
          <Icon icon="mdi:chevron-left" />
        </IconButton>
        <IconButton size="small" onClick={() => step(1)} aria-label={t('calendar.next')}>
          <Icon icon="mdi:chevron-right" />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {heading}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, next: View | null) => next && setView(next)}
        >
          <ToggleButton value={Views.DAY}>{t('calendar.day')}</ToggleButton>
          <ToggleButton value={Views.WEEK}>{t('calendar.week')}</ToggleButton>
          <ToggleButton value={Views.MONTH}>{t('calendar.month')}</ToggleButton>
          <ToggleButton value={Views.AGENDA}>{t('calendar.agenda')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <RemoteContent isLoading={lessons.isLoading} error={lessons.error}>
        <LessonsCalendar
          view={view}
          date={date}
          lessons={lessons.data?.lessons ?? []}
          availability={availability.data?.openBooking ? [] : (availability.data?.expanded ?? [])}
          onView={setView}
          onNavigate={setDate}
          onSelectLesson={(booking) => navigate(`/bookings/${booking.bookingId}`, { state: linkState })}
          onSelectSlot={async (start, end) => {
            // Days in the month view open the dialog: pick weekdays, a range.
            if (view === Views.MONTH) return setDraft(draftForDays(start, end))
            // A span in the week or day view is the offer: saved as it is drawn.
            const from = clubParts(start)
            const to = clubParts(end)
            if (from.date !== to.date) return setDraft(draftForDays(start, end))
            try {
              await add.mutateAsync([
                { kind: 'window', startsAt: clubInstant(from.date, from.time), endsAt: clubInstant(to.date, to.time) },
              ])
              showSnackbar(
                t('lessons.windowAdded', {
                  when: `${from.date} ${from.time}–${to.time}`,
                }),
              )
            } catch (err) {
              showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
            }
          }}
        />
        {(lessons.data?.lessons.length ?? 0) === 0 && !lessons.isLoading ? (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('lessons.noLessons')}
          </Typography>
        ) : null}
      </RemoteContent>

      {availability.data?.openBooking ? null : (
        <Fab
          color="primary"
          variant={phone ? 'circular' : 'extended'}
          aria-label={t('lessons.addAvailability')}
          onClick={() => setDraft(blank())}
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 32 },
            // Just clear of the phone's bottom bar; the school area has no button of its own.
            bottom: {
              xs: `calc(${BOTTOM_BAR + 16}px + env(safe-area-inset-bottom))`,
              md: 32,
            },
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Icon icon="mdi:plus" width={22} style={{ marginRight: phone ? 0 : 8 }} />
          {phone ? null : t('lessons.addAvailability')}
        </Fab>
      )}

      <Box sx={{ mt: 4 }}>
        {user ? (
          <AvailabilityPanel
            instructorUserId={user.id}
            availability={availability.data}
            draft={draft}
            onDraftHandled={clearDraft}
          />
        ) : null}
      </Box>
    </Box>
  )
}
